/**
 * Google and Meta browser tags for paid ads (E12 P3, #586).
 *
 * One browser event only: "Lead", fired after the Revenue API intake has
 * STORED a lead (its 202), never on a click. It carries the submission's
 * `client_event_id` so the platform deduplicates it against the server-side
 * upload of the same lead (P5 for Google, P6 for Meta). A qualified lead or a
 * customer is never reported from here: those are server stages (P2), not
 * form submits.
 *
 * Both tags run only with marketing consent and only while
 * `NEXT_PUBLIC_AD_ATTRIBUTION_ENABLED` is "true". The Google tag also needs an
 * approved tag id; without one it is never loaded.
 */
import { adAttributionEnabled } from "./ad-touch";
import { readConsent } from "./cookie-consent";

/** `AW-` followed by digits, from the founder. Empty means the tag is off. */
export const GOOGLE_ADS_TAG_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_TAG_ID ?? "";
/** `AW-…/<label>` of the approved "Lead" conversion action. */
export const GOOGLE_ADS_LEAD_SEND_TO = process.env.NEXT_PUBLIC_GOOGLE_ADS_LEAD_SEND_TO ?? "";

export function googleAdsTagEnabled(): boolean {
  return adAttributionEnabled() && /^AW-\d{6,15}$/.test(GOOGLE_ADS_TAG_ID);
}

type TagWindow = Window & {
  fbq?: (...args: unknown[]) => void;
  gtag?: (...args: unknown[]) => void;
};

/**
 * Report one stored lead to the platforms. Safe to call on every 202:
 * without marketing consent, or with the feature off, it does nothing.
 */
export function fireLeadEvent(clientEventId: string): void {
  try {
    if (!adAttributionEnabled() || !clientEventId) return;
    if (readConsent()?.marketing !== true) return;
    const w = window as TagWindow;
    w.fbq?.("track", "Lead", {}, { eventID: clientEventId });
    if (googleAdsTagEnabled() && /^AW-\d{6,15}\/[A-Za-z0-9_-]+$/.test(GOOGLE_ADS_LEAD_SEND_TO)) {
      w.gtag?.("event", "conversion", { send_to: GOOGLE_ADS_LEAD_SEND_TO, transaction_id: clientEventId });
    }
  } catch {
    // Never let a tag break a submission the visitor already completed.
  }
}
